import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import CallRecordingPlayer from './CallRecordingPlayer';
import { VoiceAiService } from '../services/voiceAiService';

const PAGE_SIZE = 20;

function formatIst(raw) {
  if (!raw) return '—';
  const s = String(raw).trim();
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  let d;
  if (us) {
    d = new Date(Date.UTC(+us[3], +us[1] - 1, +us[2], +us[4], +us[5], +(us[6] || 0)));
  } else {
    d = new Date(s);
  }
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return '';
  const seconds = n > 10000 ? Math.round(n / 1000) : Math.round(n);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function pageWindow(current, totalPages, span = 2) {
  if (totalPages <= 1) return [1];
  const pages = [];
  const start = Math.max(1, current - span);
  const end = Math.min(totalPages, current + span);
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('…');
  }
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push('…');
    pages.push(totalPages);
  }
  return pages;
}

function CallRow({ call }) {
  const [src, setSrc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pending, setPending] = useState(false);

  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
  }, [src]);

  const play = async () => {
    if (src || busy) return;
    setBusy(true);
    setErr('');
    const result = await VoiceAiService.fetchRecording({ callId: call.call_id });
    setBusy(false);
    if (result.ok && result.blob) {
      setSrc(URL.createObjectURL(result.blob));
      return;
    }
    setPending(!!result.pending);
    setErr(result.error || 'Recording nahi mili');
  };

  const duration = formatDuration(call.duration);
  const phone = call.from_number || call.to_number || '—';

  return (
    <div className="rounded-lg border border-slate-100 px-3 py-2 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-slate-900">
            {phone}
            {duration ? <span className="text-slate-500 font-normal"> · {duration}</span> : null}
          </p>
          <p className="text-xs text-slate-500">
            {formatIst(call.time_iso || call.time)}
            {call.call_id ? ` · #${call.call_id}` : ''}
            {call.status ? ` · ${call.status}` : ''}
          </p>
        </div>
        {!src ? (
          <button
            type="button"
            onClick={play}
            disabled={busy}
            className="text-xs font-medium text-violet-700 px-2 py-1 rounded border border-violet-200 hover:bg-violet-50 disabled:opacity-50"
          >
            {busy ? 'Loading…' : 'Play'}
          </button>
        ) : null}
      </div>
      {src ? <CallRecordingPlayer src={src} className="w-full" showDownload compact /> : null}
      {pending ? (
        <p className="text-xs text-slate-500">Recording process ho rahi hai. Thodi der baad try kijiye.</p>
      ) : null}
      {err && !src ? <p className="text-xs text-red-600">{err}</p> : null}
    </div>
  );
}

export default function VoiceAiCallList({ fullPage = false }) {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rangeStart = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, total);

  const load = useCallback(async (nextPage) => {
    setLoading(true);
    setError('');
    try {
      const res = await VoiceAiService.listCallRecordings({ page: nextPage, pagesize: PAGE_SIZE });
      setTotal(Number(res.total) || 0);
      setPage(res.page || nextPage);
      setCalls(res.calls);
    } catch (e) {
      setError(e.message || 'Recordings load nahi hui');
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const goTo = (next) => {
    const target = Math.min(totalPages, Math.max(1, next));
    if (target === page && !loading) return;
    load(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900">{fullPage ? `${total || 0} calls` : 'Call recordings'}</h2>
        <button
          type="button"
          onClick={() => load(page)}
          className="text-xs text-slate-500 inline-flex items-center gap-1"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        {fullPage ? 'Play se recording suno.' : 'OmniDimension pe jo bhi call aayi, yahan recording. Job bane ya na bane.'}
        {total ? ` Showing ${rangeStart}–${rangeEnd} of ${total}.` : ''}
      </p>
      {error ? <p className="text-sm text-red-600 mb-2">{error}</p> : null}
      {loading && calls.length === 0 ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-violet-600" size={22} />
        </div>
      ) : (
        <div className={fullPage ? 'space-y-2' : 'space-y-2 max-h-[32rem] overflow-y-auto'}>
          {calls.length === 0 ? (
            <p className="text-sm text-slate-500">Abhi koi call recording nahi hai.</p>
          ) : (
            calls.map((c) => <CallRow key={`${c.call_id}-${page}`} call={c} />)
          )}
        </div>
      )}
      {total > PAGE_SIZE ? (
        <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">
            Page {safePage} / {totalPages}
            {loading ? ' · Loading…' : ''}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => goTo(safePage - 1)}
              disabled={safePage <= 1 || loading}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronLeft size={14} />
              Prev
            </button>
            {pageWindow(safePage, totalPages).map((item, i) => (
              item === '…' ? (
                <span key={`e-${i}`} className="px-1 text-xs text-slate-400">…</span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => goTo(item)}
                  disabled={loading}
                  className={`min-w-[32px] rounded-lg px-2 py-1.5 text-xs font-semibold ${
                    item === safePage
                      ? 'bg-violet-600 text-white'
                      : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                  } disabled:opacity-40`}
                >
                  {item}
                </button>
              )
            ))}
            <button
              type="button"
              onClick={() => goTo(safePage + 1)}
              disabled={safePage >= totalPages || loading}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
